import useEmblaCarousel from 'embla-carousel-react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import { type ReactNode, useEffect, useMemo, useState } from 'react';

import ChevronIcon from '~/assets/icons/Chevron';

const VIEWPORT_EDGE_INSET = 'calc(50% - 50vw + 24px)';

type Props = {
  children: ReactNode;
  className?: string;
  itemsPerPage?: number;
  slidesClassName?: string;
};

export default function CarouselScroller({
  children,
  className,
  itemsPerPage = 4,
  slidesClassName
}: Props) {
  const plugins = useMemo(
    () => [
      WheelGesturesPlugin({
        forceWheelAxis: 'x'
      })
    ],
    []
  );
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: 'start',
      dragFree: true,
      skipSnaps: true,
      slidesToScroll: itemsPerPage
    },
    plugins
  );
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);

  useEffect(() => {
    if (!emblaApi) return;

    const update = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
      setIsScrollable(emblaApi.canScrollPrev() || emblaApi.canScrollNext());
    };

    update();
    emblaApi.on('select', update);
    emblaApi.on('reInit', update);

    return () => {
      emblaApi.off('select', update);
      emblaApi.off('reInit', update);
    };
  }, [emblaApi]);

  return (
    <div className={`relative w-full${className ? ` ${className}` : ''}`}>
      <div ref={emblaRef} className="overflow-x-clip max-md:-mx-4 max-md:pr-4">
        <div
          className={`flex max-md:px-4${slidesClassName ? ` ${slidesClassName}` : ''}`}
        >
          {children}
        </div>
      </div>
      {isScrollable && (
        <>
          <button
            type="button"
            onClick={() => emblaApi?.scrollPrev()}
            aria-label="Scroll left"
            disabled={!canScrollPrev}
            style={{
              left: VIEWPORT_EDGE_INSET,
              transform: 'translateY(calc(-50% - 40px)) rotate(90deg)'
            }}
            className="absolute top-1/2 z-[60] rounded-full bg-black/20 p-2 hover:bg-black/40 disabled:opacity-40 disabled:hover:bg-black/20 max-md:hidden min-[1220px]:!left-[-56px]"
          >
            <ChevronIcon height="16" width="16" stroke="white" />
          </button>
          <button
            type="button"
            onClick={() => emblaApi?.scrollNext()}
            aria-label="Scroll right"
            disabled={!canScrollNext}
            style={{
              right: VIEWPORT_EDGE_INSET,
              transform: 'translateY(calc(-50% - 40px)) rotate(-90deg)'
            }}
            className="absolute top-1/2 z-[60] rounded-full bg-black/20 p-2 hover:bg-black/40 disabled:opacity-40 disabled:hover:bg-black/20 max-md:hidden min-[1220px]:!right-[-56px]"
          >
            <ChevronIcon height="16" width="16" stroke="white" />
          </button>
        </>
      )}
    </div>
  );
}
